import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { Bar, BarChart, XAxis, YAxis, Cell, ResponsiveContainer } from "recharts";
import { StageBreakdown, WORKFLOW_STAGES } from "@/hooks/useCycleTimeAnalytics";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface WorkflowStageBreakdownProps {
  breakdown: StageBreakdown[];
  isLoading: boolean;
}

export function WorkflowStageBreakdown({ breakdown, isLoading }: WorkflowStageBreakdownProps) {

  const totalDays = breakdown.reduce((sum, s) => sum + s.avgDays, 0);

  // Prepare chart data for horizontal stacked bar
  const chartData = [
    {
      name: 'Workflow',
      ...Object.fromEntries(breakdown.map(s => [s.key, s.avgDays])),
    },
  ];

  const chartConfig = Object.fromEntries(
    WORKFLOW_STAGES.map(stage => [
      stage.key,
      { label: stage.label, color: stage.color },
    ])
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Workflow Stage Breakdown</CardTitle>
          <CardDescription>Average time spent in each workflow stage</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-16 w-full" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6, 7].map(i => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (breakdown.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Workflow Stage Breakdown</CardTitle>
          <CardDescription>Average time spent in each workflow stage</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No data available for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workflow Stage Breakdown</CardTitle>
        <CardDescription>Average time spent in each workflow stage</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Horizontal Stacked Bar */}
        <div className="h-20">
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 80, left: 5, bottom: 5 }}
                barSize={40}
              >
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <ChartTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-popover border rounded-lg shadow-lg p-3 space-y-2">
                        {payload.map((entry: any) => {
                          const stage = WORKFLOW_STAGES.find(s => s.key === entry.dataKey);
                          if (!stage) return null;
                          const pct = totalDays > 0 ? ((entry.value / totalDays) * 100).toFixed(0) : 0;
                          return (
                            <div key={entry.dataKey} className="space-y-0.5">
                              <div className="flex items-center gap-2 text-sm">
                                <div 
                                  className="w-3 h-3 rounded-sm" 
                                  style={{ backgroundColor: stage.color }}
                                />
                                <span className="font-medium">{stage.label}:</span>
                                <span>{entry.value.toFixed(1)} days ({pct}%)</span>
                              </div>
                              <p className="text-xs text-muted-foreground pl-5">{stage.description}</p>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }}
                />
                {WORKFLOW_STAGES.map(stage => (
                  <Bar
                    key={stage.key}
                    dataKey={stage.key}
                    stackId="a"
                    fill={stage.color}
                    radius={stage.key === 'prep_publishing' ? [4, 0, 0, 4] : 
                           stage.key === 'client_review' ? [0, 4, 4, 0] : 0}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
          <div className="text-right text-sm font-medium text-muted-foreground -mt-4 mr-2">
            Total: {totalDays.toFixed(1)} days
          </div>
        </div>

        {/* Legend */}
        <TooltipProvider>
          <div className="flex flex-wrap gap-3 justify-center">
            {WORKFLOW_STAGES.map(stage => (
              <Tooltip key={stage.key}>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 text-xs cursor-help">
                    <div 
                      className="w-3 h-3 rounded-sm" 
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="text-muted-foreground">{stage.label}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="text-xs">{stage.description}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>

        {/* Breakdown Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Stage</TableHead>
                <TableHead className="text-right">Avg Days</TableHead>
                <TableHead className="text-right">% of Total</TableHead>
                <TableHead className="text-right">Median Days</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {breakdown.map(stage => (
                <TableRow key={stage.key}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-sm flex-shrink-0" 
                        style={{ backgroundColor: stage.color }}
                      />
                      <span className="font-medium">{stage.label}</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            <p className="text-xs font-normal">{stage.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {stage.avgDays.toFixed(1)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {stage.percentOfTotal.toFixed(0)}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {stage.medianDays.toFixed(1)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
